/**
 * Client for the sf-api migration endpoints (all need eng:migrations).
 *
 *   GET  /admin/eng/migrations                 { running, migrations }
 *   GET  /admin/eng/migrations/:id/runs?limit&offset  newest first, with totalCount
 *   POST /admin/eng/migrations/:id/dry-run     202, the started run
 *   POST /admin/eng/migrations/:id/run         202, the started run; 409 DRY_RUN_REQUIRED
 *
 * Both starts answer 409 MIGRATION_RUNNING while any run is in progress on this environment, and
 * 503 MIGRATIONS_UNAVAILABLE when sf-api cannot guarantee one run at a time just now.
 * The data provider's `custom` unwraps sf-api's `{ success, data }`, so hooks expose the payload
 * at `query.data?.data`.
 */
import { useCustom, useCustomMutation } from '@refinedev/core';
import type { QueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import {
  CONFLICT_MESSAGES,
  MIGRATIONS_POLL_MS,
  MIGRATION_RUNS_LIMIT,
  type MigrationStatus,
  type RunMode,
  type RunState,
} from '../constants';

export const MIGRATIONS_BASE = 'admin/eng/migrations';

export interface MigrationRun {
  runId: string;
  migrationId: string;
  mode: RunMode;
  status: RunState;
  codeVersion: string;
  startedBy: { email: string | null; userId: string | null };
  startedAt: string | null;
  finishedAt: string | null;
  result: Record<string, unknown> | null;
  errorMessage: string | null;
}

export interface Migration {
  id: string;
  ticket: string | null;
  description: string;
  codeVersion: string;
  status: MigrationStatus;
  lastApply: MigrationRun | null;
  lastDryRun: MigrationRun | null;
  canApply: boolean;
}

export interface MigrationsList {
  running: MigrationRun | null;
  migrations: Migration[];
}

const quiet = { errorNotification: false as const, successNotification: false as const };

export const migrationsQueryKey = ['sf-admin', 'eng-migrations', 'list'] as const;
/** Every page of one migration's runs; add the offset for one page. */
export const migrationRunsQueryKey = (id: string) => ['sf-admin', 'eng-migrations', 'runs', id];

/**
 * Read a migration's history again. Cancelling first matters when the first history read is
 * still in flight: invalidating alone would join that older read, and its pre-run answer would
 * then count as fresh.
 */
export async function refreshMigrationRuns(queryClient: QueryClient, id: string): Promise<void> {
  await queryClient.cancelQueries({ queryKey: migrationRunsQueryKey(id) });
  await queryClient.invalidateQueries({ queryKey: migrationRunsQueryKey(id) });
}

const migrationPath = (id: string) => `/${MIGRATIONS_BASE}/${encodeURIComponent(id)}`;

/** Read again only while a run is in progress, never in a hidden tab. */
export const listRefetchInterval = (list: MigrationsList | undefined) =>
  list?.running ? MIGRATIONS_POLL_MS : false;

export function useMigrations() {
  return useCustom<MigrationsList>({
    url: `/${MIGRATIONS_BASE}`,
    method: 'get',
    queryOptions: {
      queryKey: [...migrationsQueryKey],
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      refetchInterval: (answer) => listRefetchInterval(answer?.data),
      refetchIntervalInBackground: false,
    },
    errorNotification: false,
  });
}

/** Read a page of runs again only while it shows a run in progress, never in a hidden tab. */
export const runsRefetchInterval = (runs: MigrationRun[] | undefined) =>
  Array.isArray(runs) && runs.some((r) => r.status === 'running') ? MIGRATIONS_POLL_MS : false;

/**
 * One page of a migration's runs. The provider keeps sf-api's outer `totalCount` as `total`
 * (`query.data?.total`), so the drawer can offer the older pages.
 */
export function useMigrationRuns(id: string | null, offset: number) {
  return useCustom<MigrationRun[]>({
    url: `${migrationPath(id ?? '')}/runs`,
    method: 'get',
    config: { query: { limit: MIGRATION_RUNS_LIMIT, offset } },
    queryOptions: {
      queryKey: [...migrationRunsQueryKey(id ?? ''), offset],
      enabled: !!id,
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      refetchInterval: (answer) => runsRefetchInterval(answer?.data),
      refetchIntervalInBackground: false,
    },
    errorNotification: false,
  });
}

/** sf-api's total number of runs from a runs answer, when it sent one. */
export const runsTotal = (answer: unknown): number | undefined => {
  const total = (answer as { total?: unknown } | undefined)?.total;
  return typeof total === 'number' && Number.isFinite(total) ? total : undefined;
};

export const useMigrationMutation = useCustomMutation;

export const startMigrationArgs = (id: string, mode: RunMode) => ({
  url: `${migrationPath(id)}/${mode === 'apply' ? 'run' : 'dry-run'}`,
  method: 'post' as const,
  values: {},
  ...quiet,
});

type ServerError = AxiosError<{ code?: string; message?: string }> & { statusCode?: number };

/** The server's own message, or the fallback; refusal codes get a clear message of their own. */
export function migrationErrorMessage(error: unknown, fallback: string): string {
  const err = error as ServerError | undefined;
  const body = err?.response?.data;
  return (body?.code && CONFLICT_MESSAGES[body.code]) || body?.message || fallback;
}
